import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {InputStatusIcon} from '@/components/ui/input-status-icon/InputStatusIcon'
import {Label} from '@/components/ui/label/Label'
import {
    SelectComboboxInput,
    SelectComboboxList,
    SelectComboboxPopover,
    SelectItem,
    SelectTrigger,
    SelectVirtualList
} from '@/components/ui/select-atoms/SelectAtoms'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {Text} from '@/components/ui/text/Text'
import {usePathologiesSearchableList} from '@/features/projects/hooks/usePathologiesSearchableList'
import {Diagnosis} from '@/features/projects/types'
import {useTranslation} from '@/translations/i18n'
import {ComboboxProvider, SelectProvider} from '@ariakit/react'
import {forwardRef, useRef, useState} from 'react'

interface InputSelectPathologyProps {
    label: string
    placeholder?: string
    value?: Diagnosis | null
    onChange: (value: Diagnosis | null) => void
    errorMessage?: string
    disabledOptions?: string[]
}

export const InputSelectPathology = forwardRef<HTMLSelectElement, InputSelectPathologyProps>(
    ({label, placeholder, value, onChange, errorMessage, disabledOptions}, ref) => {
        const {t} = useTranslation()
        const [isQueryEnabled, setIsQueryEnabled] = useState(false)
        const inputRef = useRef(null)
        const {pathologies, isLoading, isError, onSearch, onResetSearch, hasNextPage, fetchNextPage} =
            usePathologiesSearchableList({
                limit: 10,
                enabled: isQueryEnabled
            })

        return (
            <ComboboxProvider setValue={onSearch} resetValueOnHide>
                <SelectProvider
                    value={value?.id ?? ''}
                    setValue={value => onChange(pathologies.find(pathology => value == pathology.id) ?? null)}
                    setOpen={isOpen => {
                        setIsQueryEnabled(isOpen)
                        onResetSearch(inputRef)
                    }}
                >
                    <Flexbox direction="column" gap={1.5} fullWidth>
                        <Label>{label}</Label>
                        <SelectTrigger
                            ref={ref}
                            placeholder={placeholder}
                            fullWidth
                            value={value?.id}
                            displayValue={() => `${value?.code} - ${value?.description}`}
                            hasError={!!errorMessage}
                        />
                        {errorMessage && (
                            <Flexbox align="center" gap={errorMessage ? 1.5 : 0}>
                                <InputStatusIcon hasError={!!errorMessage} />
                                {errorMessage && <InputHelpText error={errorMessage} />}
                            </Flexbox>
                        )}
                        <SelectComboboxPopover sameWidth>
                            <SelectComboboxInput ref={inputRef} placeholder={t('commons:search_placeholder')} />
                            <SelectComboboxList>
                                {isLoading ? (
                                    <>
                                        {Array.from({length: 6}, (_, index) => (
                                            <Skeleton key={index} height={32} />
                                        ))}
                                    </>
                                ) : isError ? (
                                    <InputHelpText helpText={t('commons:no_results')} />
                                ) : pathologies.length > 0 ? (
                                    <SelectVirtualList
                                        data={pathologies}
                                        style={{height: 200}}
                                        totalCount={25}
                                        overscan={100}
                                        endReached={() => hasNextPage && fetchNextPage()}
                                        itemContent={(itemIndex, item) => (
                                            <SelectItem
                                                key={itemIndex}
                                                value={item.id}
                                                disabled={disabledOptions?.includes(item.id)}
                                                hasItemCheckbox
                                            >
                                                <Flexbox gap={2}>
                                                    <Text size="textSm" style={{minWidth: 50}}>
                                                        {item.code}
                                                    </Text>
                                                    <Text palette="neutral" shade="500" size="textSm">
                                                        {item.description}
                                                    </Text>
                                                </Flexbox>
                                            </SelectItem>
                                        )}
                                    />
                                ) : (
                                    <InputHelpText helpText={t('commons:no_results')} />
                                )}
                            </SelectComboboxList>
                        </SelectComboboxPopover>
                    </Flexbox>
                </SelectProvider>
            </ComboboxProvider>
        )
    }
)
